import React from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { ClaimHelper, isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { Dialog, LoadingIndicator } from "../../../assets/vfx";
import { PushNotificationPage } from "./pushNotificationPage";
import { PushNotificationForm } from "./pushNotificationForm";
import { PushNotificationView } from "./pushNotificationView";
import { PushNotificationService } from "./pushNotificationService";
import { RequestPushNotificationDto } from "../../../services/dto";
import { roles } from "../../../assets/configuration";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const PushNotificationContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { communityId: routeCommunityId } = useParams<{
    communityId?: string;
  }>();

  const claimCommunityId = !routeCommunityId
    ? ClaimHelper.getCommunityId()
    : undefined;

  const [data, setData] = React.useState<RequestPushNotificationDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const [communityId, setCommunityId] = React.useState<string | undefined>(
    routeCommunityId ?? claimCommunityId
  );

  const [notificationTypeId, setNotificationTypeId] = React.useState<
    string | undefined
  >();
    const [showDialog, setShowDialog] = React.useState<boolean>(false);

  const pushNotificationQuery =
    PushNotificationService.getPushNotificationQuery(
      data?.id ?? Constants.emptyGuid
    );
  const lookupQuery = PushNotificationService.getLookups();
  const { mutateAsync: saveRssFeed } = PushNotificationService.save();
  const { mutate: cancelNotificationMutate } =
        PushNotificationService.cancelPushNotification();
  const { mutate: deleteMutate } =
        PushNotificationService.deletePushNotification();
  const hasExistingId: boolean = !!(data?.id && data.id !== Constants.emptyGuid);
  const isEditMode: boolean = drawerType === GRID_ACTION_TYPES.EDIT && hasExistingId;
  const isCopyMode: boolean = drawerType === GRID_ACTION_TYPES.COPY;
  const closeDrawer = (): void => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }
  const isSystemAdmin = ClaimHelper.hasRole([roles.SystemAdmin]);

  const refreshGrid = (): void => {
    isEditMode && pushNotificationQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["pushNotificationGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: RequestPushNotificationDto) => {
    const payload: RequestPushNotificationDto = {
        ...values,
        id: isCopyMode ? Constants.emptyGuid : values.id,
    };

    const message = isEditMode ? "Push notification updated" : "Push notification saved";

    return await saveRssFeed(payload, { onSuccess: (response) => handleApiResponse(response, message),});
  };

  const handleCancelled = (): void => {
    cancelNotificationMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) =>
        handleApiResponse(response, "Push notification cancelled"),
    });
  };

    const handleDelete = (): void => {
        deleteMutate(data?.id ?? Constants.emptyGuid, {
            onSuccess: (response) =>
                handleApiResponse(response, "Push notification deleted"),
        });
    };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as RequestPushNotificationDto);
      setSearchAction(actionType, dataId);
    }
      if (actionType == "delete") {
          setData(({ id: dataId } as RequestPushNotificationDto));
          setShowDialog(true);
      }
  };
  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const handleEdit = (): void => {
    setData(pushNotificationQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, pushNotificationQuery.data?.id);
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <PushNotificationForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            notificationTypeId={notificationTypeId}
            disableCommunity={!isSystemAdmin}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <PushNotificationForm
            data={pushNotificationQuery.data}
            loading={pushNotificationQuery.isLoading}
            show={!!pushNotificationQuery.data}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            notificationTypeId={notificationTypeId}
            disableCommunity={!isSystemAdmin}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        const source = pushNotificationQuery.data;
        const typeDisplay = lookupQuery?.data?.notificationType?.find((x) => x.value === source?.notificationTypeId)?.display ?? "";
        const canCopy = ["Information", "Urgent"].includes(typeDisplay.trim());
        return (
          <PushNotificationView
            data={pushNotificationQuery?.data}
            show={!!pushNotificationQuery.data}
            onHide={closeDrawer}
            onCancel={handleCancelled}
            onEdit={handleEdit}
            onCopy={canCopy ? () => setDrawerType(GRID_ACTION_TYPES.COPY) : undefined}
            lookups={lookupQuery?.data}
          />
        );
      case GRID_ACTION_TYPES.COPY: {
        const source: RequestPushNotificationDto | undefined = pushNotificationQuery.data;
        const copied: RequestPushNotificationDto | undefined = source
            ? {
                ...source,
                id: Constants.emptyGuid,
                scheduledTimeUtc: new Date(),
            }
            : undefined;

        return (
            <PushNotificationForm
                data={copied}
                loading={pushNotificationQuery.isLoading}
                show={true}
                onHide={closeDrawer}
                onSave={handleSubmit}
                lookups={lookupQuery?.data}
                communityId={communityId}
                notificationTypeId={notificationTypeId}
                disableCommunity={!isSystemAdmin}
            />
        );
      }

      default:
        return null;
    }
  };

  if (lookupQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <PushNotificationPage
        onCustomEvent={handleCustomEvent}
        communityId={routeCommunityId ?? claimCommunityId}
        notificationTypeId={notificationTypeId}
        lookups={lookupQuery?.data}
        setCommunity={setCommunityId}
        setNotificationType={setNotificationTypeId}
        hideTitle={!!routeCommunityId}
      />
      {renderDrawerContent()}
          {
              <Dialog
                  contentText={"Are you sure you want to delete?"}
                  contentTitle={"Delete"}
                  onClose={() => setShowDialog(false)}
                  onConfirm={() => {
                      setShowDialog(false);
                      handleDelete();
                  }}
                  open={showDialog}
                  closeText={"No, Go Back"}
                  confirmText={"Yes, Continue"}
              />
          }
    </>
  );
};
